import type { LiveComponent, PropsWithChildren } from '@use-gpu/live';
import type { VectorLike } from '@use-gpu/core';

import { useProp } from '@use-gpu/traits/live';
import { parsePosition } from '@use-gpu/parse';
import { use, useContext, useMemo, useOne, incrementVersion } from '@use-gpu/live';
import { makeProjectionMatrix, makeOrbitMatrix, makeViewUniforms, updateViewProjection, updateViewSize, DOLLY_CUTOFF } from '@use-gpu/core';
import { RenderContext } from '../providers/render-provider';
import { ViewProvider } from '../providers/view-provider';

const DEFAULT_ORBIT_CAMERA = {
  bearing: 0,
  pitch: 0,
  roll: 0,
  
  radius: 5,

  focus: 5,
  dolly: 1,

  fov: Math.PI / 3,
  near: 0.001,
  far: 1000,
};

export type OrbitCameraProps = PropsWithChildren<{
  bearing?: number,
  pitch?: number,
  roll?: number,

  radius?: number,
  target?: VectorLike,

  fov?: number,
  near?: number,
  far?: number,
  dolly?: number,

  focus?: number,
  scale?: number | null,
}>;

export const OrbitCamera: LiveComponent<OrbitCameraProps> = (props) => {
  const {
    width,
    height,
    pixelRatio,
  } = useContext(RenderContext);

  const {
    bearing = DEFAULT_ORBIT_CAMERA.bearing,
    pitch   = DEFAULT_ORBIT_CAMERA.pitch,
    roll    = DEFAULT_ORBIT_CAMERA.roll,
    radius  = DEFAULT_ORBIT_CAMERA.radius,
    fov     = DEFAULT_ORBIT_CAMERA.fov,
    near    = DEFAULT_ORBIT_CAMERA.near,
    far     = DEFAULT_ORBIT_CAMERA.far,
    dolly   = DEFAULT_ORBIT_CAMERA.dolly,
    focus   = DEFAULT_ORBIT_CAMERA.focus,
    scale   = null,
    children,
  } = props;

  if ('phi' in props || 'theta' in props) console.warn('[OrbitCamera] Warning: `phi` and `theta` were renamed to `bearing` and `pitch`');

  const target = useProp(props.target, parsePosition);

  const uniforms = useOne(makeViewUniforms);

  const unit = scale != null ? height / pixelRatio / scale : 1;
  const ratio = pixelRatio * unit;
  const tfov2 = Math.tan(fov / 2);

  const effectiveR = dolly > DOLLY_CUTOFF ? dolly : radius;
  const effectiveTan = effectiveR * tfov2 * 2;
  const effectiveFocus = focus / effectiveR;

  const [projectionMatrix, nearFar] = makeProjectionMatrix(width, height, fov, near, far, radius, dolly);
  const viewMatrix = makeOrbitMatrix(radius, bearing, pitch, roll, target, dolly);

  updateViewProjection(
    uniforms,
    projectionMatrix,
    viewMatrix,
    undefined,
    nearFar,
  );
  updateViewSize(uniforms, width, height, ratio, effectiveTan / height, effectiveFocus, 1);

  const frameRef = useOne(() => ({current: 0}));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => frameRef.current = incrementVersion(frameRef.current), [...projectionMatrix, ...viewMatrix]);

  return use(ViewProvider, {
    frame: frameRef.current,
    uniforms,
    children,
  });
};
